import { Box } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import {
  ExpandedMultiSelect,
  FlexCol,
  FlexRow,
  FormSelect,
  MultiSelect,
  NoraButton,
  Textarea,
} from '../../../../common/components/v2';
import { Decision } from '../../../../common/constants/decision';
import { Reason } from '../../../../common/constants/reasons';
import { Resource } from '../../../../common/constants/resource';
import { Metadata } from '../../../../common/dataTypes';
import ElementLocators from '../../../../common/ElementLocators';
import { IDecision } from '../../interfaces/IDecision';
import { ClrCheckbox } from '../components/ClrCheckbox';
import { DecisionUpdateReasons } from '../decisionUpdateReasons';
import { MIBCodebacks, pickLatestNoteMibCodes } from '../MIBCodebacks';
import styles from '../styles.module.scss';
import useDeclineForm, { DeclineFormData } from './useDeclineForm';

const decisionUpdateOptions = Object.values(DecisionUpdateReasons).map(
  value => ({ value, label: value }),
);

const baseDecisionOptions = [
  { value: Decision.DECLINED, label: Resource.DECLINE_OPTION },
  { value: Decision.APPROVED, label: Resource.APPROVE_OPTION },
];

const piaDecisionOptions = [
  { value: Decision.POSTPONED, label: Resource.POSTPONE_OPTION },
  { value: Decision.INELIGIBLE, label: Resource.INELIGIBLE_OPTION },
  { value: Decision.WAIVED, label: Resource.WAIVED_OPTION },
];

const mapReasonOption = (reason: Reason) => ({
  value: reason.key,
  label: reason.displayName,
});

const mapDeclineConditionOption = (condition: Metadata) => ({
  value: condition.name,
  label: condition.displayText,
});

const DeclineForm = ({
  isUpdate,
  isPIA,
  reasons,
  declineConditions,
  isOpen,
  onSuccess,
  initialFormData,
  onDecisionChanged,
  editedDecision,
}: {
  isUpdate: boolean;
  isPIA: boolean;
  reasons: Reason[];
  declineConditions: Metadata[];
  isOpen: boolean;
  onSuccess: (formData: DeclineFormData) => void;
  initialFormData?: DeclineFormData | null;
  onDecisionChanged?: (decision: Decision) => void;
  editedDecision?: IDecision;
}) => {
  const { handleSubmit, control, clear, reset, watch, setValue } =
    useDeclineForm({
      isUpdate,
    });

  const decision = watch('decision');
  const latestNoteCodes = useSelector(pickLatestNoteMibCodes);

  const decisionOptions = useMemo(() => {
    if (!isPIA) return baseDecisionOptions;
    return [...baseDecisionOptions, ...piaDecisionOptions];
  }, [isPIA]);

  const reasonsOptions = useMemo(() => reasons.map(mapReasonOption), [reasons]);
  const declineConditionsOptions = useMemo(
    () => declineConditions.map(mapDeclineConditionOption),
    [declineConditions],
  );

  useEffect(() => {
    if (isOpen) return;
    clear();
  }, [isOpen, clear]);

  useEffect(() => {
    if (!initialFormData) return;
    reset(initialFormData);
  }, [reset, initialFormData]);

  useEffect(() => {
    if (!editedDecision || editedDecision.haltClr === undefined) return;
    setValue('triggerClr', !editedDecision.haltClr);
    setValue('haltClrReasons', editedDecision.haltClrReasons || []);
  }, [editedDecision, setValue]);

  useEffect(() => {
    if (!isOpen || latestNoteCodes.length === 0) return;
    const current = watch('mibCodes') ?? [];
    const manual = current.filter(code => !latestNoteCodes.includes(code));
    setValue('mibCodes', [...latestNoteCodes, ...manual], {
      shouldValidate: true,
    });
  }, [isOpen, latestNoteCodes, setValue, watch]);

  useEffect(() => {
    onDecisionChanged?.(decision);
  }, [onDecisionChanged, decision]);

  return (
    <Box display="flex" flexDirection="column" gap={2} className={styles.DrawerFormContent}>
      <Box flex={1} display="flex" flexDirection="column" gap={3}>
        {isUpdate && (
          <>
            <FlexRow>
              <FlexCol xs={12}>
                <Controller
                  control={control}
                  name="updateReason"
                  render={({ field, fieldState }) => (
                    <FormSelect
                      required
                      label="Reason for update"
                      options={decisionUpdateOptions}
                      error={fieldState.error?.message}
                      {...field}
                    />
                  )}
                />
              </FlexCol>
            </FlexRow>
            <FlexRow>
              <FlexCol xs={12}>
                <Controller
                  control={control}
                  name="decision"
                  render={({ field, fieldState }) => (
                    <FormSelect
                      required
                      label="Decision"
                      options={decisionOptions}
                      error={fieldState.error?.message}
                      {...field}
                    />
                  )}
                />
              </FlexCol>
            </FlexRow>
          </>
        )}
        {isPIA && (
          <ClrCheckbox
            control={control}
            editedDecisionType={editedDecision?.decisionType}
          />
        )}
        <FlexRow>
          <FlexCol xs={12}>
            <Controller
              control={control}
              name="mibCodes"
              render={({ field, fieldState }) => (
                <MIBCodebacks
                  label="Mib codes"
                  error={fieldState.error?.message}
                  {...field}
                />
              )}
            />
          </FlexCol>
        </FlexRow>
        <FlexRow>
          <FlexCol xs={12}>
            <Controller
              control={control}
              name="reasons"
              render={({ field, fieldState }) => (
                <ExpandedMultiSelect
                  required
                  options={reasonsOptions}
                  label="Decline reasons"
                  error={fieldState.error?.message}
                  {...field}
                />
              )}
            />
          </FlexCol>
        </FlexRow>
        <FlexRow>
          <FlexCol xs={12}>
            <Controller
              control={control}
              name="declineDescription"
              render={({ field, fieldState }) => (
                <Textarea
                  required
                  label="Decline description"
                  error={fieldState.error?.message}
                  {...field}
                />
              )}
            />
          </FlexCol>
        </FlexRow>
        <FlexRow>
          <FlexCol xs={12}>
            <Controller
              control={control}
              name="declineConditions"
              render={({ field, fieldState }) => (
                <MultiSelect
                  label="Decline conditions"
                  options={declineConditionsOptions}
                  error={fieldState.error?.message}
                  slotProps={{ inputLabel: { shrink: true } }}
                  {...field}
                />
              )}
            />
          </FlexCol>
        </FlexRow>
      </Box>
      <NoraButton
        className={styles.DrawerButton}
        variant="primary"
        name={ElementLocators.LOCATOR_REVIEW_DECLINE_BUTTON}
        onClick={handleSubmit(onSuccess)}
      >
        {isUpdate ? Resource.REVIEW_DECISION : Resource.REVIEW_APPLICATION}
      </NoraButton>
    </Box>
  );
};

export default DeclineForm;
